import { jsx as _jsx } from "react/jsx-runtime";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import { createContext, useContext, useEffect, useState } from "react";
import { ref, update as updateRealtimeData } from "firebase/database";
import { auth, db, realtimeDb } from "../firebase/config";
export const SUBSCRIPTION_REQUIRED_MESSAGE = "A subscription is required to use this feature.";
export function hasActiveSubscriptionAccess(profile) {
    const subscription = Number(profile?.subscription ?? 0);
    return subscription === -2 || subscription === -1 || subscription === 2;
}
const AuthContext = createContext(undefined);
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const revenueCatBaseUrl = process.env.REVENUE_CAT_BASE_URL ?? "";
const revenueCatApiKey = process.env.REVENUE_CAT_API_KEY ?? "";
export async function fetchRevenueCatSubscriber(userId) {
    if (!revenueCatBaseUrl || !revenueCatApiKey)
        throw new Error("RevenueCat is not configured.");
    const response = await fetch(`${revenueCatBaseUrl.replace(/\/$/, "")}/${encodeURIComponent(userId)}`, {
        headers: { Authorization: `Bearer ${revenueCatApiKey}` },
    });
    if (response.status === 404)
        return null;
    if (!response.ok)
        throw new Error("RevenueCat subscription details could not be loaded.");
    return response.json();
}
function parseRevenueCatDate(value) {
    if (!value)
        return 0;
    const milliseconds = Date.parse(value);
    return Number.isFinite(milliseconds) ? milliseconds : 0;
}
async function revenueCatSubscriptionUpdate(userId, currentTime) {
    if (!revenueCatBaseUrl || !revenueCatApiKey)
        return null;
    try {
        const data = await fetchRevenueCatSubscriber(userId);
        if (!data)
            return { subscription: 0 };
        const fullAccess = data.subscriber?.entitlements?.fullaccess;
        const expiresAt = parseRevenueCatDate(fullAccess?.expires_date);
        if (fullAccess && (!expiresAt || expiresAt > currentTime)) {
            return { subscription: 2, subscriptionEnds: expiresAt };
        }
        return { subscription: 0 };
    }
    catch {
        return null;
    }
}
async function makeOwnedPublicTeamsPrivate(userId) {
    try {
        const ownedTeamsSnapshot = await getDocs(query(collection(db, "teams"), where("owner.id", "==", userId)));
        const publicOwnedTeams = ownedTeamsSnapshot.docs.filter((teamDocument) => teamDocument.data().public === true);
        if (publicOwnedTeams.length === 0)
            return;
        const batch = writeBatch(db);
        const accessUpdates = {};
        publicOwnedTeams.forEach((teamDocument) => {
            batch.update(teamDocument.ref, { public: false });
            accessUpdates[`access/${teamDocument.id}/public`] = false;
        });
        await Promise.all([
            batch.commit(),
            updateRealtimeData(ref(realtimeDb), accessUpdates),
        ]);
    }
    catch (error) {
        console.warn("Owned public teams could not be made private after subscription ended.", error);
    }
}
function timestampToMillis(value) {
    if (typeof value === "number")
        return value;
    return value?.toMillis() ?? 0;
}
async function createVerifiedProfile(user) {
    const authCreated = Date.parse(user.metadata.creationTime ?? "");
    const created = Number.isFinite(authCreated) ? authCreated : Date.now();
    await setDoc(doc(db, "users", user.uid), {
        coach: true,
        created,
        email: user.email ?? "",
        enabled: true,
        id: user.uid,
        lastLogin: Date.now(),
        name: user.displayName ?? "User",
        subscription: -2,
        subscriptionEnds: created + 14 * 24 * 60 * 60 * 1000,
    });
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    function updateProfileData(updates) {
        setProfile((current) => current ? { ...current, ...updates } : current);
    }
    useEffect(() => {
        let active = true;
        const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
            if (!active)
                return;
            setLoading(true);
            if (!nextUser) {
                setUser(null);
                setProfile(null);
                setIsAdmin(false);
                setLoading(false);
                return;
            }
            setAuthError("");
            try {
                const token = await nextUser.getIdTokenResult(true);
                const admin = token.claims.admin === true;
                if (admin) {
                    if (active) {
                        setProfile(null);
                        setUser(nextUser);
                        setIsAdmin(true);
                        setLoading(false);
                    }
                    return;
                }
                if (!nextUser.emailVerified) {
                    if (active) {
                        setProfile(null);
                        setUser(nextUser);
                        setIsAdmin(false);
                        setLoading(false);
                    }
                    return;
                }
                const userRef = doc(db, "users", nextUser.uid);
                let userSnapshot = await getDoc(userRef);
                if (!userSnapshot.exists()) {
                    await createVerifiedProfile(nextUser);
                    userSnapshot = await getDoc(userRef);
                }
                if (!userSnapshot.exists())
                    throw new Error("Your user profile could not be found.");
                const loadedProfile = { id: userSnapshot.id, ...userSnapshot.data() };
                const currentTime = Date.now();
                const userUpdates = { lastLogin: currentTime };
                const subscription = loadedProfile.subscription ?? 0;
                const subscriptionEnds = timestampToMillis(loadedProfile.subscriptionEnds);
                if (subscription === -2) {
                    if (subscriptionEnds > 0 && currentTime > subscriptionEnds) {
                        userUpdates.subscription = 0;
                        loadedProfile.subscription = 0;
                    }
                }
                else if (subscription !== -1) {
                    const revenueCatUpdate = await revenueCatSubscriptionUpdate(nextUser.uid, currentTime);
                    if (revenueCatUpdate) {
                        userUpdates.subscription = revenueCatUpdate.subscription;
                        loadedProfile.subscription = revenueCatUpdate.subscription;
                        if (typeof revenueCatUpdate.subscriptionEnds === "number") {
                            userUpdates.subscriptionEnds = revenueCatUpdate.subscriptionEnds;
                            loadedProfile.subscriptionEnds = revenueCatUpdate.subscriptionEnds;
                        }
                    }
                    else if (subscriptionEnds > 0 && currentTime > subscriptionEnds) {
                        userUpdates.subscription = 0;
                        loadedProfile.subscription = 0;
                    }
                }
                await updateDoc(userRef, userUpdates);
                if (Number(loadedProfile.subscription ?? 0) === 0) {
                    await makeOwnedPublicTeamsPrivate(nextUser.uid);
                }
                if (!loadedProfile.enabled)
                    throw new Error("This account has been disabled.");
                if (active) {
                    setProfile(loadedProfile);
                    setUser(nextUser);
                    setIsAdmin(admin);
                    setLoading(false);
                }
            }
            catch (error) {
                if (active) {
                    setUser(null);
                    setProfile(null);
                    setIsAdmin(false);
                    setAuthError(error instanceof Error ? error.message : "Your user profile could not be loaded.");
                    setLoading(false);
                }
                await signOut(auth);
            }
        });
        return () => {
            active = false;
            unsubscribe();
        };
    }, []);
    useEffect(() => {
        if (!user)
            return;
        const timeout = isAdmin ? 15 * 60 * 1000 : INACTIVITY_TIMEOUT_MS;
        let timeoutId = window.setTimeout(() => void signOut(auth), timeout);
        const activityEvents = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart", "focus"];
        function resetInactivityTimer() {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => void signOut(auth), timeout);
        }
        activityEvents.forEach((eventName) => window.addEventListener(eventName, resetInactivityTimer));
        return () => {
            window.clearTimeout(timeoutId);
            activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetInactivityTimer));
        };
    }, [isAdmin, user]);
    return _jsx(AuthContext.Provider, { value: { user, profile, loading, authError, isAdmin, hasSubscriptionAccess: isAdmin || hasActiveSubscriptionAccess(profile), subscriptionRequiredMessage: SUBSCRIPTION_REQUIRED_MESSAGE, updateProfileData }, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context)
        throw new Error("useAuth must be used within AuthProvider");
    return context;
}
